import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FilePlus2, Lock, Send } from "lucide-react";
import { from, rpc, translateError, unwrap, useAction, useHasRole, useOrg } from "@vixio/data";
import {
  AuditTimeline, Button, Callout, ConfirmDialog, DataTable, DescriptionList, EmptyState, KpiRow, KpiTile, PageHeader, Panel, Skeleton,
  StatusFromMap, StatusPill, Tabs, formatDateTime, formatInt,
} from "@vixio/ui";
import { AggregateView } from "./AggregateView";
import { VersionEditorDialog } from "./DefinitionEditor";
import { FIELD_LABEL, FORM_STATUS, IDENTITY_LABEL, KIND_LABEL, optionLabel, type FormItem, type FormRow, type FormVersion } from "./api";

/** Contagem exata sem trazer linhas (Content-Range). */
export async function countRows(q: PromiseLike<{ count: number | null; error: unknown }>): Promise<number> {
  const r = await q;
  if (r.error) throw translateError(r.error);
  return r.count ?? 0;
}

export function useForm(id: string, orgId: string) {
  return useQuery({
    queryKey: ["forms", "form", id],
    queryFn: () => unwrap<FormRow>(from("forms", "forms").select("*").eq("id", id).eq("org_id", orgId).single()),
  });
}

export function FormPage() {
  const { id = "" } = useParams();
  const org = useOrg();
  const navigate = useNavigate();
  const canWrite = useHasRole("member");
  const isAdmin = useHasRole("admin");
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [publishing, setPublishing] = useState<FormVersion | null>(null);

  const form = useForm(id, org.id);
  const versions = useQuery({
    queryKey: ["forms", "versions", id],
    queryFn: () => unwrap<FormVersion[]>(from("forms", "form_versions").select("*").eq("form_id", id).order("version", { ascending: false })),
  });
  const responses = useQuery({
    queryKey: ["forms", "responses-count", id],
    queryFn: () => countRows(from("forms", "responses").select("id", { count: "exact", head: true }).eq("form_id", id)),
  });

  const publish = useAction((v: string) => rpc("forms", "publish_version", { p_version: v }), { invalidate: [["forms"]], success: "Versão publicada" });
  const close = useAction(() => rpc("forms", "close_form", { p_form: id }), { invalidate: [["forms"]], success: "Formulário encerrado" });

  if (form.isLoading) return <div className="vx-content"><Skeleton /></div>;
  if (!form.data) {
    return (
      <div className="vx-content">
        <Panel>
          <EmptyState title="Formulário não encontrado" actions={<Button onClick={() => navigate("/diagnostico/formularios")}>Voltar</Button>} />
        </Panel>
      </div>
    );
  }
  const f = form.data;
  const vs = versions.data ?? [];
  const current = vs.find((v) => v.id === f.current_version_id) ?? null;
  const latest = vs[0] ?? null;
  const draft = vs.find((v) => !v.published_at) ?? null;
  const items: FormItem[] = (current ?? latest)?.definition.items ?? [];
  const dims = new Map(((current ?? latest)?.definition.dimensions ?? []).map((d) => [d.key, d.label ?? d.key]));
  const scale = (current ?? latest)?.definition.scale;

  return (
    <div className="vx-content">
      <PageHeader
        crumbs={[{ label: "Formulários", to: "/diagnostico/formularios" }, { label: f.title }]}
        title={f.title}
        meta={
          <>
            <StatusFromMap map={FORM_STATUS} value={f.status} />
            <StatusPill plain>{KIND_LABEL[f.kind]}</StatusPill>
            <span className="mono text-2">{f.slug}</span>
          </>
        }
        actions={
          <>
            {canWrite && f.status !== "closed" ? (
              <Button icon={<FilePlus2 />} onClick={() => setEditing(true)} disabled={Boolean(draft)} title={draft ? "Publique ou descarte o rascunho antes" : undefined}>
                Nova versão
              </Button>
            ) : null}
            {isAdmin && draft ? (
              <Button variant="primary" icon={<Send />} onClick={() => setPublishing(draft)}>
                Publicar v{draft.version}
              </Button>
            ) : null}
            {isAdmin && f.status === "published" ? (
              <Button variant="danger" icon={<Lock />} onClick={() => setClosing(true)}>
                Encerrar
              </Button>
            ) : null}
          </>
        }
      />
      <KpiRow>
        <KpiTile label="Versão vigente" value={current ? `v${current.version}` : "nenhuma"} foot={current?.published_at ? `publicada ${formatDateTime(current.published_at)}` : "sem versão publicada"} />
        <KpiTile label="Respostas" value={formatInt(responses.data ?? 0)} foot="gravadas e imutáveis" />
        <KpiTile label="Identidade" value={IDENTITY_LABEL[f.identity_mode]} foot={f.identity_mode === "anonymous" ? "sem pessoa nas respostas" : "com consentimento por finalidade"} />
        <KpiTile label="k mínimo" value={formatInt(f.min_group_size)} foot="para qualquer agregado" />
      </KpiRow>
      {f.status === "closed" ? (
        <div className="vx-section">
          <Callout tone="warn" title="Encerrado">
            Não aparece mais no site e não recebe respostas. Publicar uma nova versão reabre o formulário.
          </Callout>
        </div>
      ) : null}
      <div className="vx-section">
        <Tabs
          label="Formulário"
          defaultValue={current ? "resultados" : "versoes"}
          tabs={[
            {
              value: "resultados",
              label: "Resultados",
              content: (
                <div style={{ paddingTop: 16 }}>
                  {current ? <AggregateView formId={f.id} scaleLabel={scale ? `${scale.min} a ${scale.max}` : undefined} /> : <p className="muted">Publique uma versão para receber respostas.</p>}
                </div>
              ),
            },
            {
              value: "itens",
              label: "Itens",
              count: items.length,
              content: (
                <div style={{ paddingTop: 16 }}>
                  <DataTable<FormItem>
                    caption={`Itens da versão ${(current ?? latest)?.version ?? ""}`}
                    data={items}
                    rowKey={(i) => i.id}
                    bare
                    empty="Sem itens: crie a primeira versão."
                    columns={[
                      { key: "id", header: "Id", cell: (i) => <span className="mono">{i.id}</span> },
                      {
                        key: "text",
                        header: "Enunciado",
                        cell: (i) => (
                          <span className="vx-table__primary">
                            {i.text}
                            {i.options?.length ? <span className="vx-table__sub">{i.options.map(optionLabel).join(" · ")}</span> : null}
                          </span>
                        ),
                      },
                      { key: "type", header: "Tipo", cell: (i) => FIELD_LABEL[i.type] ?? i.type },
                      { key: "dimension", header: "Dimensão", cell: (i) => (i.dimension ? dims.get(i.dimension) ?? i.dimension : <span className="muted">-</span>) },
                      { key: "required", header: "Obrigatório", cell: (i) => (i.required === false ? "não" : "sim") },
                    ]}
                  />
                </div>
              ),
            },
            {
              value: "versoes",
              label: "Versões",
              count: vs.length,
              content: (
                <div style={{ paddingTop: 16 }}>
                  <DataTable<FormVersion>
                    caption="Versões"
                    data={vs}
                    loading={versions.isLoading}
                    rowKey={(v) => v.id}
                    bare
                    empty={canWrite ? "Nenhuma versão. Use Nova versão para definir os itens." : "Nenhuma versão."}
                    columns={[
                      { key: "version", header: "Versão", cell: (v) => <span className="num">v{v.version}</span> },
                      { key: "items", header: "Itens", align: "right", cell: (v) => <span className="num">{v.definition.items?.length ?? 0}</span> },
                      { key: "scoring", header: "Pontuação", cell: (v) => (v.scoring ? "sim" : "não") },
                      { key: "created_at", header: "Criada", kind: "datetime" },
                      {
                        key: "published_at",
                        header: "Situação",
                        cell: (v) =>
                          v.id === f.current_version_id ? (
                            <StatusPill tone="ok">vigente</StatusPill>
                          ) : v.published_at ? (
                            <StatusPill plain>publicada {formatDateTime(v.published_at)}</StatusPill>
                          ) : (
                            <StatusPill tone="warn">rascunho</StatusPill>
                          ),
                      },
                      {
                        key: "actions",
                        header: <span className="sr-only">Ações</span>,
                        align: "right",
                        cell: (v) =>
                          isAdmin && !v.published_at ? (
                            <Button size="sm" variant="primary" onClick={() => setPublishing(v)}>
                              Publicar
                            </Button>
                          ) : null,
                      },
                    ]}
                  />
                </div>
              ),
            },
            {
              value: "dados",
              label: "Dados",
              content: (
                <div className="vx-grid vx-grid--2" style={{ paddingTop: 16 }}>
                  <Panel title="Configuração">
                    <DescriptionList
                      items={[
                        ["Slug", <span className="mono">{f.slug}</span>],
                        ["Tipo", KIND_LABEL[f.kind]],
                        ["Identidade", IDENTITY_LABEL[f.identity_mode]],
                        ["Abre em", f.opens_at ? formatDateTime(f.opens_at) : "sem data"],
                        ["Fecha em", f.closes_at ? formatDateTime(f.closes_at) : "sem data"],
                        ["Criado em", formatDateTime(f.created_at)],
                      ]}
                    />
                  </Panel>
                  {isAdmin ? (
                    <Panel title="Auditoria">
                      <AuditTimeline orgId={org.id} entityId={f.id} limit={20} />
                    </Panel>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      </div>
      <VersionEditorDialog form={f} base={(current ?? latest)?.definition ?? null} open={editing} onOpenChange={setEditing} />
      <ConfirmDialog
        open={publishing !== null}
        onOpenChange={(o) => !o && setPublishing(null)}
        title={`Publicar a versão ${publishing?.version ?? ""}`}
        confirmLabel="Publicar"
        onConfirm={() => publish.mutateAsync(publishing!.id)}
      >
        <p>
          A versão fica imutável e passa a ser a vigente: o site e as integrações usam esta definição a partir de agora. Respostas antigas continuam
          ligadas à versão em que foram dadas.
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        open={closing}
        onOpenChange={setClosing}
        title="Encerrar formulário"
        confirmLabel="Encerrar"
        destructive
        confirmText="ENCERRAR"
        onConfirm={() => close.mutateAsync(undefined)}
      >
        <p>O formulário sai do site e para de receber respostas. As respostas e os agregados continuam aqui.</p>
      </ConfirmDialog>
    </div>
  );
}
